import PopUpMenu from "../PopUpMenu/PopUpMenu";
import MenuItem from "@mui/material/MenuItem";
import LegalbirdIoModal from "../Modal/LegalbirdIoModal";
import Button from "@mui/material/Button";
import ChangeMediaObjectDescriptionModal from "../ElectronicFileFolder/ChangeMediaObjectDescriptionModal";
import ChangeMediaObjectVisibilityModal from "../ElectronicFileFolder/ChangeMediaObjectVisibilityModal";
import ChangeMediaObjectFolderModal from "../ElectronicFileFolder/ChangeMediaObjectFolderModal";
import ChangeMediaObjectIdentifierModal from "../ElectronicFileFolder/ChangeMediaObjectIdentifierModal";
import CopyMediaObjectModal from "../ElectronicFileFolder/CopyMediaObjectModal";
import ChangeFieldNameModal from "../ElectronicFileFolder/ChangeFieldNameModal";
import React, { useState } from "react";
import { useCurrentUser } from "../../provider/CurrentUserProvider";
import { MediaObject } from "../../types/MediaObject";
import { useMutation } from "@tanstack/react-query";
import { updateResource } from "../../services/ReactQuery/reactQueryService";
import { useCase } from "../../provider/CaseProvider";
import { getChangeFieldNameModalOptions } from "../../services/mediaObjectService";
import { userHasOneOfTheseRoles, userHasRole } from "../../services/backofficeUserService";
import { Roles } from "../../types/BackofficeUser";
import { apiPost } from "../../services/Api/apiCall";
import { useSnackbar } from "notistack";
import LegalbirdIoConfirm from "../Modal/LegalbirdIoConfirm";
import SummarizeAndInformMediaObjectModal from "../ElectronicFileFolder/SummarizeAndInformMediaObjectModal";
import { AutoAwesomeOutlined } from "@mui/icons-material";
import { ListItemIcon, ListItemText, Typography } from "@mui/material";
import MergeMediaObjectsModal from "./MergeMediaObjectsModal";
import DocumentClassLabelAiProvider from "../../provider/DocumentClassLabelAiProvider";
import ActionIdentifierProvider from "../../provider/ActionIdentifierProvider";
import { getMediaObjectMergeRestrictionLabel } from "../../services/mediaObjectMergeService";

type MediaObjectPopUpMenuProps = {
  mediaObject: MediaObject;
  updateMediaObjects: Function;
};

const MediaObjectPopUpMenu = ({ mediaObject, updateMediaObjects }: MediaObjectPopUpMenuProps) => {
  const [deleteMediaObjectOpen, setDeleteMediaObjectOpen] = useState(false);
  const [changeDescriptionOpen, setChangeDescriptionOpen] = useState(false);
  const [changeVisibilityOpen, setChangeVisibilityOpen] = useState(false);
  const [summarizeAndInformOpen, setSummarizeAndInformOpen] = useState(false);
  const [contentSummaryOpen, setContentSummaryOpen] = useState(false);
  const [selectSubfolderOpen, setSelectSubfolderOpen] = useState(false);
  const [changeIdentifierOpen, setChangeIdentifierOpen] = useState(false);
  const [copyMediaObjectOpen, setCopyMediaObjectOpen] = useState(false);
  const [changeFieldNameOpen, setChangeFieldNameOpen] = useState(false);
  const [compressMediaObjectOpen, setCompressMediaObjectOpen] = useState(false);
  const [mergeMediaObjectsOpen, setMergeMediaObjectsOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const updateMutation = useMutation({
    mutationFn: updateResource,
  });
  const currentUser = useCurrentUser();

  const deleteAllowed = currentUser;
  const isExternal = userHasRole(currentUser, Roles.external);
  const canViewContentSummary = userHasOneOfTheseRoles(currentUser, [
    Roles.developer,
    Roles.admin,
    Roles.productManager,
  ]);

  const { product } = useCase();

  const handleDeleteMediaObjectClick = () => {
    updateMutation
      .mutateAsync({
        uri: "media_objects",
        id: mediaObject.id,
        data: {
          deleted: true,
          showInGallery: false,
          showInExternalView: false,
        },
      })
      .then(() => updateMediaObjects());
    setDeleteMediaObjectOpen(false);
  };

  const handleMarkAsFavourite = (isFavourite: boolean) => {
    updateMutation
      .mutateAsync({
        uri: "media_objects",
        id: mediaObject.id,
        data: {
          isFavourite: isFavourite,
        },
      })
      .then(() => updateMediaObjects());
  };

  const sendMediaObjectCompressionRequest = async () => {
    await apiPost(`lb/services/media_object_compression_request/${mediaObject.id}`).then(() => {
      updateMediaObjects();
      enqueueSnackbar("Komprimierung abgeschlossen.", {
        variant: "custom",
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    });
  };

  return (
    <React.Fragment key={`mediaObjectPopUpMenu-${mediaObject.id}`}>
      <PopUpMenu>
        {canViewContentSummary && (
          <MenuItem disabled={!mediaObject.contentSummary} onClick={() => setContentSummaryOpen(true)}>
            Inhaltszusammenfassung anzeigen
          </MenuItem>
        )}
        <MenuItem onClick={() => setChangeDescriptionOpen(true)}>Beschreibung ändern</MenuItem>
        {!isExternal && <MenuItem onClick={() => setChangeVisibilityOpen(true)}>Sichtbarkeit bearbeiten</MenuItem>}
        {!isExternal &&
          userHasOneOfTheseRoles(currentUser, [
            Roles.admin,
            Roles.lawyer,
            Roles.traineeLawyer,
            Roles.productManager,
            Roles.accountmanager,
          ]) && (
            <MenuItem onClick={() => setSummarizeAndInformOpen(true)}>
              <ListItemText>Zusammenfassen & Informieren</ListItemText>
              <ListItemIcon>
                <AutoAwesomeOutlined fontSize={"small"} color={"primary"} />
              </ListItemIcon>
            </MenuItem>
          )}
        {!isExternal && (
          <MenuItem onClick={() => setSelectSubfolderOpen(true)}>{"In anderen Ordner schieben"}</MenuItem>
        )}
        {!isExternal && <MenuItem onClick={() => setChangeIdentifierOpen(true)}>Art des Dokuments ändern</MenuItem>}
        {!isExternal && mediaObject.mimeType === "application/pdf" && mediaObject.downloadOriginalUrl === null && (
          <MenuItem onClick={() => setCompressMediaObjectOpen(true)}>Komprimieren</MenuItem>
        )}
        {!isExternal && <MenuItem onClick={() => setCopyMediaObjectOpen(true)}>Als Anlage duplizieren</MenuItem>}
        {!isExternal && (
          <MenuItem
            disabled={!product || mediaObject.mergeRestriction !== null}
            onClick={() => setMergeMediaObjectsOpen(true)}
          >
            <ListItemText
              primary="Mit anderen Dokumenten zusammenführen"
              secondary={getMediaObjectMergeRestrictionLabel(mediaObject.mergeRestriction)}
            />
          </MenuItem>
        )}
        {!isExternal && product && getChangeFieldNameModalOptions(product, mediaObject).length > 0 && (
          <MenuItem onClick={() => setChangeFieldNameOpen(true)}>
            Markierung als außergerichtl. Schreiben ändern
          </MenuItem>
        )}
        <MenuItem component="a" download={mediaObject.originalName} href={mediaObject.downloadUrl}>
          Herunterladen
        </MenuItem>
        {mediaObject.downloadOriginalUrl && (
          <MenuItem component="a" download={mediaObject.originalName} href={mediaObject.downloadOriginalUrl}>
            Originalversion herunterladen
          </MenuItem>
        )}
        {!isExternal && (
          <MenuItem onClick={() => handleMarkAsFavourite(!mediaObject.isFavourite)}>
            {mediaObject.isFavourite ? "Als Favorit entfernen" : "Als Favorit markieren"}
          </MenuItem>
        )}
        {deleteAllowed && <MenuItem onClick={() => setDeleteMediaObjectOpen(true)}>Löschen</MenuItem>}
      </PopUpMenu>
      {canViewContentSummary && (
        <LegalbirdIoModal
          open={contentSummaryOpen}
          handleClose={() => setContentSummaryOpen(false)}
          title="Inhaltszusammenfassung"
          cancelButtonLabel="Schließen"
          fullWidth
        >
          <Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
            {mediaObject.contentSummary}
          </Typography>
        </LegalbirdIoModal>
      )}
      {deleteAllowed && (
        <LegalbirdIoModal
          handleClose={() => setDeleteMediaObjectOpen(false)}
          open={deleteMediaObjectOpen}
          title={"Datei löschen?"}
          submitButton={
            <Button variant={"contained"} onClick={handleDeleteMediaObjectClick}>
              Ja
            </Button>
          }
        >
          <p className={"center"}>
            Diese Datei wird unwiderruflich gelöscht. Sind Sie sicher, dass Sie die Datei löschen möchten?
          </p>
        </LegalbirdIoModal>
      )}
      <ChangeMediaObjectDescriptionModal
        setChangeDescriptionOpen={setChangeDescriptionOpen}
        changeDescriptionOpen={changeDescriptionOpen}
        mediaObject={mediaObject}
        updateMediaObjects={updateMediaObjects}
      />
      <ChangeMediaObjectVisibilityModal
        mediaObject={mediaObject}
        updateMediaObjects={updateMediaObjects}
        changeVisibilityOpen={changeVisibilityOpen}
        setChangeVisibilityOpen={setChangeVisibilityOpen}
      />
      <SummarizeAndInformMediaObjectModal
        key={"summarizeAndInformOpen" + summarizeAndInformOpen}
        mediaObject={mediaObject}
        summarizeAndInformOpen={summarizeAndInformOpen}
        setSummarizeAndInformOpen={setSummarizeAndInformOpen}
      />
      <ChangeMediaObjectFolderModal
        setSelectSubfolderOpen={setSelectSubfolderOpen}
        updateMediaObjects={updateMediaObjects}
        mediaObject={mediaObject}
        selectSubfolderOpen={selectSubfolderOpen}
      />
      <ChangeMediaObjectIdentifierModal
        setChangeIdentifierOpen={setChangeIdentifierOpen}
        changeIdentifierOpen={changeIdentifierOpen}
        mediaObject={mediaObject}
        updateMediaObjects={updateMediaObjects}
      />
      <CopyMediaObjectModal
        setCopyMediaObjectOpen={setCopyMediaObjectOpen}
        copyMediaObjectOpen={copyMediaObjectOpen}
        mediaObject={mediaObject}
        updateMediaObjects={updateMediaObjects}
      />
      <ChangeFieldNameModal
        mediaObject={mediaObject}
        updateMediaObjects={updateMediaObjects}
        changeFieldNameOpen={changeFieldNameOpen}
        setChangeFieldNameOpen={setChangeFieldNameOpen}
      />
      <LegalbirdIoConfirm
        open={compressMediaObjectOpen}
        headline={"PDF komprimieren"}
        content={"Mit Klick auf “PDF komprimieren” kann die Dateigröße des Dokuments verringert werden."}
        confirmText={"PDF komprimieren"}
        handleClose={() => setCompressMediaObjectOpen(false)}
        handleConfirm={sendMediaObjectCompressionRequest}
      />
      {mergeMediaObjectsOpen && product && (
        <DocumentClassLabelAiProvider>
          <ActionIdentifierProvider>
            <MergeMediaObjectsModal
              initialMediaObject={mediaObject}
              product={product}
              handleClose={() => setMergeMediaObjectsOpen(false)}
              handleSuccess={() => updateMediaObjects()}
            />
          </ActionIdentifierProvider>
        </DocumentClassLabelAiProvider>
      )}
    </React.Fragment>
  );
};

export default MediaObjectPopUpMenu;
